import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const validateMaterialForm = (form) => {
  const errors = {};
  if (!form.materialName?.trim()) {
    errors.materialName = 'Material name is required.';
  }
  if (!form.refNum?.trim()) {
    errors.refNum = 'Reference number is required.';
  }
  return errors;
};

const initialForm = {
  materialName: '',
  refNum: '',
  truckType: '',
};

const getCustomerId = (row) => String(row?.customerName?._id || row?.customerName || '');

const Materials = ({ forcedCustomerId = '', embedded = false }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const [createForm, setCreateForm] = useState(initialForm);
  const [editForm, setEditForm] = useState(initialForm);

  const user = useMemo(() => getStoredUser(), []);
  const canManage = user?.role === 'admin' || user?.role === 'internal' || user?.role === 'customer';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchMaterials = useCallback(async () => {
    try {
      const query = forcedCustomerId ? `?customerId=${forcedCustomerId}` : '';
      const response = await fetch(`${API_BASE_URL}/api/materials${query}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      const scopedRows = forcedCustomerId
        ? rows.filter((row) => getCustomerId(row) === String(forcedCustomerId))
        : rows;
      setMaterials(scopedRows);
      setLoading(false);
    } catch (err) {
      setLoadError(err.message);
      setLoading(false);
    }
  }, [forcedCustomerId]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const openCreate = () => setCreateOpen(true);

  const closeCreate = () => {
    setCreateOpen(false);
    setActionError('');
    setCreateFieldErrors({});
    setCreateForm(initialForm);
  };

  const openEdit = (material) => {
    setSelectedMaterial(material);
    setEditFieldErrors({});
    setEditForm({
      materialName: material.materialName || '',
      refNum: material.refNum || '',
      truckType: material.truckType || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedMaterial(null);
    setActionError('');
    setEditFieldErrors({});
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
    setCreateFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const saveCreate = async () => {
    const errors = validateMaterialForm(createForm);
    setCreateFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/materials`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...createForm,
          ...(forcedCustomerId ? { customerName: forcedCustomerId } : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to create material');
      }

      const data = await response.json();
      setMaterials((prev) => [data.material, ...prev]);
      closeCreate();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const saveEdit = async () => {
    if (!selectedMaterial?._id) return;

    const errors = validateMaterialForm(editForm);
    setEditFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/materials/${selectedMaterial._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to update material');
      }

      const data = await response.json();
      setMaterials((prev) => prev.map((m) => (m._id === data.material._id ? data.material : m)));
      closeEdit();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const deactivateMaterial = async (materialId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/materials/${materialId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to deactivate material');
      }

      const data = await response.json();
      setMaterials((prev) => prev.map((m) => (m._id === data.material._id ? data.material : m)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading) return <LoadingPane label="Loading materials..." />;

  return (
    <Box p={embedded ? 0 : 2}>
      {!embedded ? (
        <PageHeader
          title="Materials"
          subtitle="Define material types, reference numbers, and truck type defaults."
          actions={canManage ? [<Button key="add" variant="contained" onClick={openCreate}>Add Material</Button>] : null}
        />
      ) : (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Materials</Typography>
          {canManage ? <Button variant="contained" onClick={openCreate}>Add Material</Button> : null}
        </Box>
      )}
      <ErrorBanner message={loadError || actionError} />

      {materials.length === 0 ? (
        <EmptyPane title="No materials found" />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {materials.map((material) => (
            <Card key={material._id} variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.6}>
                  <Typography fontWeight={700}>{material.materialName}</Typography>
                  <Chip size="small" label={material.isActive === false ? 'Inactive' : 'Active'} color={material.isActive === false ? 'default' : 'success'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">Ref #: {material.refNum || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Truck Type: {material.truckType || 'N/A'}</Typography>
                {canManage && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit material"><IconButton color="primary" onClick={() => openEdit(material)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Deactivate material"><span><IconButton color="warning" onClick={() => deactivateMaterial(material._id)} disabled={material.isActive === false}><BlockIcon /></IconButton></span></Tooltip>
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#ffffff' }}>
              <TableCell>Material</TableCell>
              <TableCell>Reference Number</TableCell>
              <TableCell>Truck Type</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>No materials found.</TableCell>
              </TableRow>
            ) : (
              materials.map((material) => (
                <TableRow key={material._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{material.materialName}</Typography>
                  </TableCell>
                  <TableCell>{material.refNum || 'N/A'}</TableCell>
                  <TableCell>{material.truckType || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={material.isActive === false ? 'Inactive' : 'Active'}
                      color={material.isActive === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Edit material">
                        <IconButton color="primary" onClick={() => openEdit(material)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate material">
                        <span>
                          <IconButton color="warning" onClick={() => deactivateMaterial(material._id)} disabled={material.isActive === false}>
                            <BlockIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Material</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="materialName" label="Material Name" value={createForm.materialName} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.materialName)} helperText={createFieldErrors.materialName} />
          <TextField margin="normal" name="refNum" label="Reference Number" value={createForm.refNum} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.refNum)} helperText={createFieldErrors.refNum} />
          <TextField margin="normal" name="truckType" label="Truck Type" value={createForm.truckType} onChange={handleCreateChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancel</Button>
          <Button variant="contained" onClick={saveCreate} disabled={saving}>{saving ? 'Saving...' : 'Create Material'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Material</DialogTitle>
        <DialogContent>
          <TextField margin="normal" name="materialName" label="Material Name" value={editForm.materialName} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.materialName)} helperText={editFieldErrors.materialName} />
          <TextField margin="normal" name="refNum" label="Reference Number" value={editForm.refNum} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.refNum)} helperText={editFieldErrors.refNum} />
          <TextField margin="normal" name="truckType" label="Truck Type" value={editForm.truckType} onChange={handleEditChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Materials;

